import { GrowthTimelineResult, TimelineYearPoint } from '../types/financialPlan';
import { calendarYearForPlanAge } from './planDates';

export type RetireAccountKey = 'k401' | 'rothIra' | 'hsa' | 'taxable';

export const RETIRE_ACCOUNT_KEYS: RetireAccountKey[] = ['k401', 'rothIra', 'hsa', 'taxable'];

export type RequiredIncomePoint = {
  age: number;
  budget: number;
  budgetNominal: number;
  debt: number;
  debtNominal: number;
  k12: number;
  k12Nominal: number;
  /** Total required save (retirement + other) — kept for plan debug compatibility */
  save: number;
  saveNominal: number;
  saveRetirement: number;
  saveRetirementNominal: number;
  saveOther: number;
  saveOtherNominal: number;
  tax: number;
  taxNominal: number;
  income: number;
  incomeNominal: number;
  grossRequired: number;
  grossRequiredNominal: number;
  /** Gross income above required gross — cap for surplus stack layers */
  incomeGap: number;
  incomeGapNominal: number;
  /** Surplus stack: retirement-directed allocation by account (gross, today's $) */
  retireSep: number;
  retireSepNominal: number;
  retireRoth: number;
  retireRothNominal: number;
  retireHsa: number;
  retireHsaNominal: number;
  retireTaxable: number;
  retireTaxableNominal: number;
  /** Take-home surplus not routed to retirement (gross equivalent, capped) */
  surplusOpen: number;
  surplusOpenNominal: number;
  /** Goal-path contributions by account (take-home annual) */
  retireContribK401: number;
  retireContribK401Nominal: number;
  retireContribRoth: number;
  retireContribRothNominal: number;
  retireContribHsa: number;
  retireContribHsaNominal: number;
  retireContribTaxable: number;
  retireContribTaxableNominal: number;
  surplusLumpK401: number;
  surplusLumpK401Nominal: number;
  surplusLumpRoth: number;
  surplusLumpRothNominal: number;
  surplusLumpHsa: number;
  surplusLumpHsaNominal: number;
  surplusLumpTaxable: number;
  surplusLumpTaxableNominal: number;
  /** Total configured + goal-path extra retirement investing (take-home annual) */
  retirementContrib: number;
  retirementContribNominal: number;
  /** Phased surplus lump sum to retirement (take-home annual, informational) */
  surplusToRetirement: number;
  surplusToRetirementNominal: number;
  /** Take-home surplus after required spend & save */
  surplusAnnual: number;
  surplusAnnualNominal: number;
  /** @deprecated use retireSep + retireRoth + retireHsa + retireTaxable */
  retireInvestExtra: number;
  retireInvestExtraNominal: number;
  /** @deprecated use account retire* fields */
  surplusRetire: number;
  surplusRetireNominal: number;
};

/** Partition take-home surplus into chart layers, capped so the stack never exceeds planned income. */
export function partitionSurplusAllocation(params: {
  incomeGross: number;
  grossRequired: number;
  surplusTakeHome: number;
  retirementContribTakeHome: number;
  saveRetirementTakeHome: number;
  surplusToRetirementTakeHome: number;
  taxRatePct: number;
}): {
  incomeGapGross: number;
  extraRetireTakeHome: number;
  surplusRetTakeHome: number;
  openTakeHome: number;
  extraRetireGross: number;
  surplusRetGross: number;
  openGross: number;
} {
  const incomeGapGross = Math.max(0, params.incomeGross - params.grossRequired);
  const surplusTakeHome = Math.max(0, params.surplusTakeHome);

  if (surplusTakeHome <= 0 || incomeGapGross <= 0) {
    return {
      incomeGapGross,
      extraRetireTakeHome: 0,
      surplusRetTakeHome: 0,
      openTakeHome: 0,
      extraRetireGross: 0,
      surplusRetGross: 0,
      openGross: 0,
    };
  }

  const extraRetireTakeHome = Math.min(
    Math.max(0, params.retirementContribTakeHome - params.saveRetirementTakeHome),
    surplusTakeHome,
  );
  const afterExtra = surplusTakeHome - extraRetireTakeHome;
  const surplusRetTakeHome = Math.min(
    Math.max(0, params.surplusToRetirementTakeHome),
    afterExtra,
  );
  const openTakeHome = Math.max(0, afterExtra - surplusRetTakeHome);

  let extraRetireGross = 0;
  let surplusRetGross = 0;
  let openGross = 0;
  if (surplusTakeHome > 0) {
    extraRetireGross = incomeGapGross * (extraRetireTakeHome / surplusTakeHome);
    surplusRetGross = incomeGapGross * (surplusRetTakeHome / surplusTakeHome);
    openGross = incomeGapGross * (openTakeHome / surplusTakeHome);
  }

  return {
    incomeGapGross,
    extraRetireTakeHome,
    surplusRetTakeHome,
    openTakeHome,
    extraRetireGross,
    surplusRetGross,
    openGross,
  };
}

function retireAccountTakeHomeAnnual(p: TimelineYearPoint, key: RetireAccountKey): number {
  switch (key) {
    case 'k401':
      return (p.retireContribK401Annual ?? 0) + (p.surplusLumpK401Annual ?? 0);
    case 'rothIra':
      return (p.retireContribRothAnnual ?? 0) + (p.surplusLumpRothAnnual ?? 0);
    case 'hsa':
      return (p.retireContribHsaAnnual ?? 0) + (p.surplusLumpHsaAnnual ?? 0);
    case 'taxable':
      return (p.retireContribTaxableAnnual ?? 0) + (p.surplusLumpTaxableAnnual ?? 0);
    default:
      return 0;
  }
}

/** Split retirement-directed surplus stack (gross) across accounts by goal-path mix. */
export function partitionRetireDirectedByAccount(
  retireDirectedGross: number,
  accountTakeHome: Record<RetireAccountKey, number>,
): Record<RetireAccountKey, number> {
  const result = { k401: 0, rothIra: 0, hsa: 0, taxable: 0 };
  if (retireDirectedGross <= 0) return result;
  const total = RETIRE_ACCOUNT_KEYS.reduce((s, k) => s + Math.max(0, accountTakeHome[k]), 0);
  if (total <= 0) {
    const share = retireDirectedGross / RETIRE_ACCOUNT_KEYS.length;
    RETIRE_ACCOUNT_KEYS.forEach((k) => {
      result[k] = share;
    });
    return result;
  }
  RETIRE_ACCOUNT_KEYS.forEach((k) => {
    result[k] = retireDirectedGross * (Math.max(0, accountTakeHome[k]) / total);
  });
  return result;
}

export function calendarYearFromPlanTimeline(timeline: GrowthTimelineResult, age: number): number {
  return calendarYearForPlanAge(
    {
      birthDate: timeline.birthDate,
      planStartDate: timeline.planStartDate,
      currentAge: timeline.currentAge,
      spouseAge: 0,
      retirementAge: timeline.retirementAge,
      inflationPct: timeline.inflationPct,
      portfolioReturnPct: 0,
      effectiveTaxRatePct: timeline.effectiveTaxRatePct ?? 0,
      safeWithdrawalRatePct: 0,
    },
    age,
  );
}

/** Required-income stack per age — shared by timeline chart and plan debug table. */
export function buildRequiredIncomeData(timeline: GrowthTimelineResult): RequiredIncomePoint[] {
  const inflation = timeline.inflationPct;
  const taxRatePct = timeline.effectiveTaxRatePct ?? 0;
  const currentAge = timeline.currentAge;
  const factorAt = (age: number) => {
    const years = age - currentAge;
    if (years <= 0 || inflation <= 0) return 1;
    return Math.pow(1 + inflation / 100, years);
  };

  return timeline.points.map((p) => {
    const emptyRetireContrib = {
      retireContribK401: 0,
      retireContribK401Nominal: 0,
      retireContribRoth: 0,
      retireContribRothNominal: 0,
      retireContribHsa: 0,
      retireContribHsaNominal: 0,
      retireContribTaxable: 0,
      retireContribTaxableNominal: 0,
      surplusLumpK401: 0,
      surplusLumpK401Nominal: 0,
      surplusLumpRoth: 0,
      surplusLumpRothNominal: 0,
      surplusLumpHsa: 0,
      surplusLumpHsaNominal: 0,
      surplusLumpTaxable: 0,
      surplusLumpTaxableNominal: 0,
    };
    const emptyRetireAlloc = {
      retireSep: 0,
      retireSepNominal: 0,
      retireRoth: 0,
      retireRothNominal: 0,
      retireHsa: 0,
      retireHsaNominal: 0,
      retireTaxable: 0,
      retireTaxableNominal: 0,
      surplusOpen: 0,
      surplusOpenNominal: 0,
      retireInvestExtra: 0,
      retireInvestExtraNominal: 0,
      surplusRetire: 0,
      surplusRetireNominal: 0,
    };

    if (p.phase !== 'accumulation') {
      return {
        age: p.age,
        budget: 0,
        budgetNominal: 0,
        debt: 0,
        debtNominal: 0,
        k12: 0,
        k12Nominal: 0,
        save: 0,
        saveNominal: 0,
        saveRetirement: 0,
        saveRetirementNominal: 0,
        saveOther: 0,
        saveOtherNominal: 0,
        tax: 0,
        taxNominal: 0,
        income: 0,
        incomeNominal: 0,
        grossRequired: 0,
        grossRequiredNominal: 0,
        incomeGap: 0,
        incomeGapNominal: 0,
        retirementContrib: 0,
        retirementContribNominal: 0,
        surplusToRetirement: 0,
        surplusToRetirementNominal: 0,
        surplusAnnual: 0,
        surplusAnnualNominal: 0,
        ...emptyRetireContrib,
        ...emptyRetireAlloc,
      };
    }

    const factor = factorAt(p.age);
    const debtNominal = p.debtServiceAnnual ?? 0;
    const k12Nominal = p.k12SpendingAnnual ?? 0;
    const spendTotalNominal = p.requiredSpendAnnual ?? 0;
    const budgetNominal = Math.max(0, spendTotalNominal - debtNominal - k12Nominal);
    const saveNominal = p.requiredSaveAnnual ?? 0;
    const saveRetirementNominal = p.saveRetirementAnnual ?? saveNominal;
    const saveOtherNominal = p.saveOtherAnnual ?? Math.max(0, saveNominal - saveRetirementNominal);
    const grossRequiredNominal = p.requiredGrossAnnual ?? 0;
    const taxNominal = Math.max(0, grossRequiredNominal - spendTotalNominal - saveNominal);
    const income = p.grossIncomeToday ?? 0;
    const incomeNominal = p.grossIncome ?? Math.round(income * factor);

    const retirementContribNominal = p.retirementInvestAnnual ?? 0;
    const surplusToRetirementNominal = p.surplusToRetirementAnnual ?? 0;
    const surplusAnnualNominal = p.surplusAnnual ?? 0;

    const allocation = partitionSurplusAllocation({
      incomeGross: incomeNominal,
      grossRequired: grossRequiredNominal,
      surplusTakeHome: surplusAnnualNominal,
      retirementContribTakeHome: retirementContribNominal,
      saveRetirementTakeHome: saveRetirementNominal,
      surplusToRetirementTakeHome: surplusToRetirementNominal,
      taxRatePct,
    });

    const retireDirectedGross = allocation.extraRetireGross + allocation.surplusRetGross;
    const accountTakeHome: Record<RetireAccountKey, number> = {
      k401: retireAccountTakeHomeAnnual(p, 'k401'),
      rothIra: retireAccountTakeHomeAnnual(p, 'rothIra'),
      hsa: retireAccountTakeHomeAnnual(p, 'hsa'),
      taxable: retireAccountTakeHomeAnnual(p, 'taxable'),
    };
    const retireByAccount = partitionRetireDirectedByAccount(retireDirectedGross, accountTakeHome);

    const contribK401Nominal = p.retireContribK401Annual ?? 0;
    const contribRothNominal = p.retireContribRothAnnual ?? 0;
    const contribHsaNominal = p.retireContribHsaAnnual ?? 0;
    const contribTaxableNominal = p.retireContribTaxableAnnual ?? 0;
    const lumpK401Nominal = p.surplusLumpK401Annual ?? 0;
    const lumpRothNominal = p.surplusLumpRothAnnual ?? 0;
    const lumpHsaNominal = p.surplusLumpHsaAnnual ?? 0;
    const lumpTaxableNominal = p.surplusLumpTaxableAnnual ?? 0;

    return {
      age: p.age,
      budget: Math.round(budgetNominal / factor),
      budgetNominal: Math.round(budgetNominal),
      debt: Math.round(debtNominal / factor),
      debtNominal: Math.round(debtNominal),
      k12: Math.round(k12Nominal / factor),
      k12Nominal: Math.round(k12Nominal),
      save: Math.round(saveNominal / factor),
      saveNominal: Math.round(saveNominal),
      saveRetirement: Math.round(saveRetirementNominal / factor),
      saveRetirementNominal: Math.round(saveRetirementNominal),
      saveOther: Math.round(saveOtherNominal / factor),
      saveOtherNominal: Math.round(saveOtherNominal),
      tax: Math.round(taxNominal / factor),
      taxNominal: Math.round(taxNominal),
      income: Math.round(income),
      incomeNominal: Math.round(incomeNominal),
      grossRequired: Math.round(grossRequiredNominal / factor),
      grossRequiredNominal: Math.round(grossRequiredNominal),
      incomeGap: Math.round(allocation.incomeGapGross / factor),
      incomeGapNominal: Math.round(allocation.incomeGapGross),
      retireSep: Math.round(retireByAccount.k401 / factor),
      retireSepNominal: Math.round(retireByAccount.k401),
      retireRoth: Math.round(retireByAccount.rothIra / factor),
      retireRothNominal: Math.round(retireByAccount.rothIra),
      retireHsa: Math.round(retireByAccount.hsa / factor),
      retireHsaNominal: Math.round(retireByAccount.hsa),
      retireTaxable: Math.round(retireByAccount.taxable / factor),
      retireTaxableNominal: Math.round(retireByAccount.taxable),
      surplusOpen: Math.round(allocation.openGross / factor),
      surplusOpenNominal: Math.round(allocation.openGross),
      retireContribK401: Math.round(contribK401Nominal / factor),
      retireContribK401Nominal: Math.round(contribK401Nominal),
      retireContribRoth: Math.round(contribRothNominal / factor),
      retireContribRothNominal: Math.round(contribRothNominal),
      retireContribHsa: Math.round(contribHsaNominal / factor),
      retireContribHsaNominal: Math.round(contribHsaNominal),
      retireContribTaxable: Math.round(contribTaxableNominal / factor),
      retireContribTaxableNominal: Math.round(contribTaxableNominal),
      surplusLumpK401: Math.round(lumpK401Nominal / factor),
      surplusLumpK401Nominal: Math.round(lumpK401Nominal),
      surplusLumpRoth: Math.round(lumpRothNominal / factor),
      surplusLumpRothNominal: Math.round(lumpRothNominal),
      surplusLumpHsa: Math.round(lumpHsaNominal / factor),
      surplusLumpHsaNominal: Math.round(lumpHsaNominal),
      surplusLumpTaxable: Math.round(lumpTaxableNominal / factor),
      surplusLumpTaxableNominal: Math.round(lumpTaxableNominal),
      retirementContrib: Math.round(retirementContribNominal / factor),
      retirementContribNominal: Math.round(retirementContribNominal),
      surplusToRetirement: Math.round(surplusToRetirementNominal / factor),
      surplusToRetirementNominal: Math.round(surplusToRetirementNominal),
      surplusAnnual: Math.round(surplusAnnualNominal / factor),
      surplusAnnualNominal: Math.round(surplusAnnualNominal),
      retireInvestExtra: Math.round(allocation.extraRetireGross / factor),
      retireInvestExtraNominal: Math.round(allocation.extraRetireGross),
      surplusRetire: Math.round(allocation.surplusRetGross / factor),
      surplusRetireNominal: Math.round(allocation.surplusRetGross),
    };
  });
}

export function requiredIncomeStackTotalNominal(row: RequiredIncomePoint): number {
  return (
    row.grossRequiredNominal +
    row.retireSepNominal +
    row.retireRothNominal +
    row.retireHsaNominal +
    row.retireTaxableNominal +
    row.surplusOpenNominal
  );
}
